import React, { useState } from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconQuestionMarkCircle from "../../../../img/icons/question-mark-circle.svg";
import {
    IDiscount,
    IOfferDiscount,
    IVoucherDiscount,
} from "../../../models/catalogue.interfaces";
import { format } from "../../../utils/format";
import { discountIsVoucher } from "../../../utils/guards";
import { Dinero, ZERO, getDinero, invert, sum } from "../../../utils/money";

import styles from "./BasketAllSavings.module.scss";

/**
 * Figure out the proper name to display for the given discount using the following rules:
 *   1. If the discount is from a voucher (coupon), use the voucher name.
 *   2. If the discount is from an offer:
 *     1. If there is a `short_name`, use it.
 *     2. Lastly, use the long name.
 */
const getDiscountName = (discount: IDiscount): string => {
    if (discountIsVoucher(discount)) {
        return discount.voucher.name;
    }
    return discount.short_name ? discount.short_name : discount.name;
};

const SavingsAccordion = (): React.ReactNode => (
    <div id="basket-savings-accordion" className={styles.accordionContent}>
        <p>{t`Promotional codes apply towards retail value only.`}</p>
        <p>
            {t`To maximize your savings, site wide offers will automatically apply to any remaining balance.`}
        </p>
    </div>
);

const SavingsRow = (props: {
    discount: IDiscount;
    removeVoucherCode: (code: string) => void;
}): React.ReactNode => {
    const discount = props.discount;
    const amount = getDinero(discount.amount);
    return (
        <ul className={styles.line}>
            <li className="basket-savings__title basket-savings__detail-title">
                <span>{getDiscountName(discount)}</span>
                {discountIsVoucher(discount) && (
                    <span>
                        <div className="basket-savings__code">
                            {t`Code:`} {discount.voucher.code}{" "}
                        </div>
                        <a
                            onClick={(event) => {
                                event.preventDefault();
                                props.removeVoucherCode(discount.voucher.code);
                            }}
                            className="basket-savings__detail-remove"
                        >
                            [{t`Remove`}]
                        </a>
                    </span>
                )}
            </li>
            {amount.greaterThan(ZERO) && (
                <li className={styles.accentText}>
                    <strong>{format.money(invert(amount))}</strong>
                </li>
            )}
        </ul>
    );
};

export const BasketAllSavings = (props: {
    offerDiscounts: IOfferDiscount[];
    voucherDiscounts: IVoucherDiscount[];
    offerPostOrderActions: IOfferDiscount[];
    voucherPostOrderActions: IVoucherDiscount[];
    removeVoucherCode: (code: string) => void;
    totalOscarDiscount: Dinero;
    hasOscarAdjustment: boolean;
}): React.ReactNode => {
    const [savingsInfoExpanded, setSavingsInfoExpanded] = useState(false);

    const onInfoClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setSavingsInfoExpanded((curr) => {
            return !curr;
        });
    };

    // Combine the offers and vouchers and sort them based on their index
    // property (to ensure proper application order)
    const allSavings: IDiscount[] = props.offerDiscounts
        .concat(props.voucherDiscounts)
        .concat(props.offerPostOrderActions)
        .concat(props.voucherPostOrderActions)
        .sort((a, b) => {
            return a.index - b.index;
        });
    const visibleSavings = allSavings.filter((discount) => !discount.is_hidden);
    const totalSavings = sum(
        allSavings.map((discount) => getDinero(discount.amount)),
    );
    if (visibleSavings.length <= 0 && totalSavings.lessThanOrEqual(ZERO)) {
        return null;
    }
    return (
        <div className={styles.root}>
            <ul className={styles.line}>
                <li>
                    <strong>{t`Total Savings`}</strong>
                    <button
                        className={`${styles.button} accordion`}
                        onClick={onInfoClick}
                        aria-controls="basket-savings-accordion"
                        aria-expanded={savingsInfoExpanded}
                        aria-label={t`More info`}
                    >
                        <SVG
                            className="basket-savings__title-icon"
                            src={iconQuestionMarkCircle}
                            role="img"
                            aria-hidden="true"
                            title={t`what is basket savings?`}
                        />
                    </button>
                </li>
                <li>
                    {/* Hide total savings amount if totalSavings is zero */}
                    {totalSavings.greaterThan(ZERO) && (
                        <strong className={styles.accentText}>
                            -{format.money(totalSavings)}
                        </strong>
                    )}
                </li>
            </ul>
            {savingsInfoExpanded && <SavingsAccordion />}
            <div>
                {props.hasOscarAdjustment && (
                    <SavingsRow
                        discount={{
                            index: 0,
                            name: t`Discount`,
                            short_name: t`Discount`,
                            description: "",
                            amount: format.decimal(props.totalOscarDiscount),
                            is_hidden: false,
                        }}
                        removeVoucherCode={props.removeVoucherCode}
                    />
                )}
                {visibleSavings.map((discount, i) => (
                    <SavingsRow
                        key={i}
                        discount={discount}
                        removeVoucherCode={props.removeVoucherCode}
                    />
                ))}
            </div>
        </div>
    );
};
